/**
 * Pivot helpers for compare-mode rows (D-020 step 4).
 *
 * The worker returns rows of the shape:
 *   `{ __period__: 'A' | 'B', <groupBy>: any, <metric_alias>: number }`
 *
 * Widgets need a two-series view:
 *   `categories: ['EU', 'US']`
 *   `seriesA:    [1000, 2000]`
 *   `seriesB:    [800,  1900]`
 *
 * This module is purely functional and independent of ECharts so the
 * tests can lock the contract without spinning up the chart engine.
 */

import type { QueryResultRow } from '@/types/query';
import { categoryKey } from '@/lib/date-format';

export type PivotedCompare = {
  /** Distinct group-by values in source order. Empty when the spec has no groupBy. */
  categories: string[];
  /** Period-A metric value per category, aligned to `categories`. */
  seriesA: number[];
  /** Period-B metric value per category, aligned to `categories`. */
  seriesB: number[];
  /** True iff at least one row tagged 'A' was found. */
  hasA: boolean;
  /** True iff at least one row tagged 'B' was found. */
  hasB: boolean;
};

/** Per-category delta — used by the comparison-table widget. */
export type CompareDeltaRow = {
  category: string;
  /** Period A value (defaults to 0 when missing). */
  valueA: number;
  /** Period B value (defaults to 0 when missing). */
  valueB: number;
  /** Absolute delta (A - B). */
  delta: number;
  /** Percent change `(A - B) / B`, `null` when B is zero. */
  percent: number | null;
};

/**
 * Build per-category delta rows from a {@link PivotedCompare} result.
 * Sorted by signed delta descending so positive movers come first
 * and negative movers last; callers slice from each end for the
 * winners + losers tables.
 */
export function buildCompareDeltas(
  pivot: PivotedCompare,
): CompareDeltaRow[] {
  const rows: CompareDeltaRow[] = pivot.categories.map((category, i) => {
    const valueA = pivot.seriesA[i] ?? 0;
    const valueB = pivot.seriesB[i] ?? 0;
    const delta = valueA - valueB;
    const percent = valueB !== 0 ? delta / valueB : null;
    return { category, valueA, valueB, delta, percent };
  });
  rows.sort((a, b) => b.delta - a.delta);
  return rows;
}

/**
 * Truncate a pivoted compare result to the top-N categories by
 * `seriesA + seriesB` descending. Selected categories are then
 * re-sorted into their original source order so a chronological X
 * axis stays chronological after truncation. No-op when `limit` is
 * `<= 0` or the pivot already has fewer categories.
 *
 * Compare-mode SQL deliberately skips LIMIT (chopping period B for a
 * low-period-A category would distort the comparison — see
 * `build-compare-sql.ts`), so widgets apply the user-chosen row cap
 * here instead, after pivoting onto aligned A/B arrays.
 */
export function truncatePivot(
  pivot: PivotedCompare,
  limit: number,
): PivotedCompare {
  if (limit <= 0 || pivot.categories.length <= limit) return pivot;
  type Ranked = { cat: string; i: number; sum: number };
  const ranked: Ranked[] = pivot.categories.map((cat, i) => ({
    cat,
    i,
    sum: (pivot.seriesA[i] ?? 0) + (pivot.seriesB[i] ?? 0),
  }));
  ranked.sort((a, b) => b.sum - a.sum);
  const top = ranked.slice(0, limit);
  // Re-sort selected by original index so the truncated axis still
  // reads in the same order the upstream SQL produced (chronological
  // for time axes, the SQL's deterministic order for the rest).
  top.sort((a, b) => a.i - b.i);
  return {
    categories: top.map((r) => r.cat),
    seriesA: top.map((r) => pivot.seriesA[r.i] ?? 0),
    seriesB: top.map((r) => pivot.seriesB[r.i] ?? 0),
    hasA: pivot.hasA,
    hasB: pivot.hasB,
  };
}

/** Period KPI summary — used by the KPI widget's compare layout. */
export type CompareKpi = {
  /** Period-A aggregate value, or `null` when missing. */
  valueA: number | null;
  /** Period-B aggregate value, or `null` when missing. */
  valueB: number | null;
  /** Absolute delta (A - B), or `null` when either side is missing. */
  delta: number | null;
  /** Percent change `(A - B) / B`, `null` when B is zero / missing. */
  percent: number | null;
};

/**
 * Pivot rows tagged with `__period__` into aligned A / B series indexed
 * by the chosen group-by column. Missing cells default to zero so the
 * chart shows a flat baseline rather than a gap; downstream code can
 * treat zero specially if desired.
 */
export function pivotCompareRows(
  rows: readonly QueryResultRow[],
  groupByColumn: string,
  metricAlias: string,
): PivotedCompare {
  const cats: string[] = [];
  const aMap = new Map<string, number>();
  const bMap = new Map<string, number>();
  let hasA = false;
  let hasB = false;

  for (const row of rows) {
    const period = row['__period__'];
    const cat = categoryKey(row[groupByColumn]);
    if (!cats.includes(cat)) cats.push(cat);
    const value = toNumber(row[metricAlias]);
    if (period === 'A') {
      aMap.set(cat, value);
      hasA = true;
    } else if (period === 'B') {
      bMap.set(cat, value);
      hasB = true;
    }
  }

  return {
    categories: cats,
    seriesA: cats.map((c) => aMap.get(c) ?? 0),
    seriesB: cats.map((c) => bMap.get(c) ?? 0),
    hasA,
    hasB,
  };
}

/**
 * Reduce compare rows for a KPI widget (no groupBy). Pulls the
 * single-row aggregate per period and computes delta + percent.
 */
export function summarizeCompareKpi(
  rows: readonly QueryResultRow[],
  metricAlias: string,
): CompareKpi {
  let valueA: number | null = null;
  let valueB: number | null = null;
  for (const row of rows) {
    const v = toNumber(row[metricAlias]);
    if (row['__period__'] === 'A') valueA = v;
    else if (row['__period__'] === 'B') valueB = v;
  }
  const delta =
    valueA != null && valueB != null ? valueA - valueB : null;
  const percent =
    valueA != null && valueB != null && valueB !== 0
      ? (valueA - valueB) / valueB
      : null;
  return { valueA, valueB, delta, percent };
}

function toNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'bigint') return Number(v);
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
